<template>
  <div class="chart-funding row" style="padding-top: 0">
    <div class="col-xs-12 col-12 col-sm-10 col-sm-offset-1 col-md-8 col-md-offset-2 offset-md-2 offset-sm-1">
      <list-legend-filter
        :value="options.selectedCountry"
        :legends="countryList"
        :legend-title="'Filter country'"
        :reset-label="'Reset all countries'"
        class="chart-funding__legend"
        @filter:legend="options.selectedCountry = $event"
      />

      <div class="chart-funding__legend-deal">
        <div class="legend-deal__icon" />
        <em class="text-legend">
          Undisclosed deals are shown as empty circles outlined in their respective country shades.
        </em>
      </div>

      <timeline-filter v-model="options.selectedYear" :items="yearList" :title="'Select year'" />

      <select-group-filter v-model="options.selectedTab" :items="clusterList" />
    </div>

    <!-- Chart area -->
    <div class="col-12 col-xs-12 col-sm-12">
      <div id="chartContainer">
        <svg id="chartSVG" />
        <div class="viz-tooltip">
          <span class="viz-tooltip-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import ListLegendFilter from '@/components/ui/ListLegendFilter.vue';
import TimelineFilter from '@/components/ui/TimelineFilter.vue';
import SelectGroupFilter from '@/components/ui/SelectGroupFilter.vue';

// utility
import * as d3 from 'd3';
import kebabCase from 'lodash-es/kebabCase';

export default {
  name: 'FundingDealsViz',
  components: {
    ListLegendFilter,
    TimelineFilter,
    SelectGroupFilter,
  },
  data() {
    return {
      countryList: [
        { name: 'Indonesia', color: '#E25A5F' },
        { name: 'Malaysia', color: '#FDC4D0' },
        { name: 'Philippines', color: '#A295E2' },
        { name: 'Singapore', color: '#005EB8' },
        { name: 'Thailand', color: '#F6C76A' },
        { name: 'Vietnam', color: '#F09252' },
      ],
      yearList: ['2016', '2017', '2018', '2019', '2020'],
      clusterList: ['All deals', 'Group by funding stage', 'Group by FinTech category'],
      options: { selectedCountry: 'All', selectedYear: '2020', selectedTab: 'All deals' },
      innerWidth: 850,
      forceStrength: 0.05,
      currentNodes: [],
    };
  },
  computed: {
    innerHeight() {
      // mobile
      if (this.$breakpoint.mobile) {
        if (this.options.selectedTab === 'All deals') {
          return 800;
        }
        if (this.options.selectedTab === 'Group by funding stage') {
          return 1200;
        }
        if (this.options.selectedTab === 'Group by FinTech category') {
          return 1850;
        }
      }
      // desktop
      if (!this.$breakpoint.mobile) {
        // All deals
        if (this.options.selectedTab === 'All deals') {
          if (this.currentNodes.length < 90) {
            return 380;
          }
          return 500;
        }
        // Funding stage 2 x 2 grid
        if (this.options.selectedTab === 'Group by funding stage') {
          // standard grid for this condition because of data
          if (this.options.selectedCountry === 'Vietnam' && this.options.selectedYear === '2019') {
            return 700;
          }
          // standard grid for this condition because of data
          if (this.options.selectedCountry === 'All' && this.options.selectedYear === '2016') {
            return 550;
          }
          // standard grid
          if (this.options.selectedCountry === 'Singapore' || this.options.selectedCountry === 'All') {
            return 700;
          }
          // small grid
          return 480;
        }
        // Fintech category 3 x 4 grid
        if (this.options.selectedTab === 'Group by FinTech category') {
          // special grid for this condition
          if (this.options.selectedCountry === 'Vietnam' && this.options.selectedYear === '2019') {
            return 800;
          }
          if (this.currentNodes.length < 40 || this.options.selectedYear === '2016') {
            return 650;
          }
          return 825;
        }
      }
      return 500;
    },
    chartMargin() {
      return { top: 10, right: 10, bottom: 10, left: 10 };
    },
    chartSettings() {
      return {
        width: this.innerWidth + this.chartMargin.left + this.chartMargin.right,
        height: this.innerHeight + this.chartMargin.top + this.chartMargin.bottom,
      };
    },
    radiusScale() {
      if (this.$breakpoint.mobile) {
        return 0.22;
      }
      return 0.45;
    },
    centerCoor() {
      return {
        Indonesia: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.73 },
        Singapore: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.74 },
        Vietnam: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.75 },
        Philippines: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.78 },
        Thailand: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.81 },
        Malaysia: { x: this.chartSettings.width / 2, y: this.chartSettings.height / 1.84 },
      };
    },
    splitCenterCoors() {
      // calculation for all
      const allValues = {
        count: this.currentNodes.length,
        sum: (
          this.currentNodes.reduce((a, { value }) => a + parseFloat(value !== 'Undisclosed' ? value : 0), 0) / 1000000
        ).toFixed(2),
      };

      // calculation for stage clusters
      const stageGroup = d3.groups(this.currentNodes, (d) => d.stage);
      const stages = ['pre', 'early', 'late', 'others'];
      const stageValues = {};
      stages.forEach((s) => {
        const c = stageGroup.find((d) => d[0] === s);
        stageValues[s] = {
          count: c ? c[1].length : 0,
          sum: c
            ? (c[1].reduce((a, { value }) => a + parseFloat(value !== 'Undisclosed' ? value : 0), 0) / 1000000).toFixed(
                2
              )
            : 0,
        };
      });

      // calculation for category
      const categoryGroup = d3.groups(this.currentNodes, (d) => d.category);
      const category = [
        'investment',
        'alternative',
        'payments',
        'banking',
        'reg',
        'blockchain',
        'cryptocurrencies',
        'finance',
        'insur',
      ];
      const categoryValues = {};
      category.forEach((s) => {
        const c = categoryGroup.find((d) => d[0] === s);
        categoryValues[s] = {
          count: c ? c[1].length : 0,
          sum: c
            ? (c[1].reduce((a, { value }) => a + parseFloat(value !== 'Undisclosed' ? value : 0), 0) / 1000000).toFixed(
                2
              )
            : 0,
        };
      });

      return {
        allText: [{ label: 'all', count: allValues.count, sum: allValues.sum, x: this.chartSettings.width / 2, y: 35 }],
        stageBubbles: {
          pre: { x: (1.5 * this.chartSettings.width) / 6, y: (1.8 * this.chartSettings.height) / 6 },
          early: { x: (4 * this.chartSettings.width) / 6, y: (1.8 * this.chartSettings.height) / 6 },
          late: { x: (1.5 * this.chartSettings.width) / 6, y: (6.15 * this.chartSettings.height) / 8 },
          others: { x: (4 * this.chartSettings.width) / 6, y: (6 * this.chartSettings.height) / 8 },
          // others: { x: -1, y: -1 },
        },
        stageText: [
          {
            count: stageValues.pre.count,
            sum: stageValues.pre.sum,
            label: 'Pre-Series',
            x: (1.4 * this.chartSettings.width) / 6,
            y: (0.15 * this.chartSettings.height) / 6,
          },
          {
            count: stageValues.early.count,
            sum: stageValues.early.sum,
            label: 'Early Stage (A-B)',
            x: (4.1 * this.chartSettings.width) / 6,
            y: (0.15 * this.chartSettings.height) / 6,
          },
          {
            count: stageValues.late.count,
            sum: stageValues.late.sum,
            label: 'Late Stage (C-E)',
            x: (1.4 * this.chartSettings.width) / 6,
            y: (4.2 * this.chartSettings.height) / 8,
          },
          {
            count: stageValues.others.count,
            sum: stageValues.others.sum,
            label: 'Others',
            x: (4.1 * this.chartSettings.width) / 6,
            y: (4.2 * this.chartSettings.height) / 8,
          },
        ],
        categoryBubbles: {
          alternative: { x: (1 * this.chartSettings.width) / 6, y: (2.7 * this.chartSettings.height) / 12 },
          banking: { x: (3 * this.chartSettings.width) / 6, y: (2.4 * this.chartSettings.height) / 12 },
          blockchain: { x: (5 * this.chartSettings.width) / 6, y: (2.4 * this.chartSettings.height) / 12 },
          cryptocurrencies: { x: (1 * this.chartSettings.width) / 6, y: (6 * this.chartSettings.height) / 12 },
          finance: { x: (3 * this.chartSettings.width) / 6, y: (6.2 * this.chartSettings.height) / 12 },
          insur: { x: (5 * this.chartSettings.width) / 6, y: (6.2 * this.chartSettings.height) / 12 },
          investment: { x: (1 * this.chartSettings.width) / 6, y: (9.7 * this.chartSettings.height) / 12 },
          payments: { x: (3 * this.chartSettings.width) / 6, y: (10.1 * this.chartSettings.height) / 12 },
          reg: { x: (5 * this.chartSettings.width) / 6, y: (9.7 * this.chartSettings.height) / 12 },
        },
        categoryText: [
          {
            count: categoryValues.alternative.count,
            sum: categoryValues.alternative.sum,
            label: 'Alternative Lending',
            x: this.chartSettings.width / 6,
            y: (0.23 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.banking.count,
            sum: categoryValues.banking.sum,
            label: 'Banking Tech',
            x: (3 * this.chartSettings.width) / 6,
            y: (0.23 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.blockchain.count,
            sum: categoryValues.blockchain.sum,
            label: 'Blockchain in Financial Services',
            x: (5 * this.chartSettings.width) / 6,
            y: (0.23 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.cryptocurrencies.count,
            sum: categoryValues.cryptocurrencies.sum,
            label: 'Cryptocurrencies',
            x: this.chartSettings.width / 6,
            y: (4.5 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.finance.count,
            sum: categoryValues.finance.sum,
            label: 'Finance & Accounting Tech',
            x: (3 * this.chartSettings.width) / 6,
            y: (4.5 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.insur.count,
            sum: categoryValues.insur.sum,
            label: 'InsurTech',
            x: (5 * this.chartSettings.width) / 6,
            y: (4.5 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.investment.count,
            sum: categoryValues.investment.sum,
            label: 'Investment Tech',
            x: (1 * this.chartSettings.width) / 6,
            y: (8 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.payments.count,
            sum: categoryValues.payments.sum,
            label: 'Payments',
            x: (3 * this.chartSettings.width) / 6,
            y: (8 * this.chartSettings.height) / 12,
          },
          {
            count: categoryValues.reg.count,
            sum: categoryValues.reg.sum,
            label: 'RegTech',
            x: (5 * this.chartSettings.width) / 6,
            y: (8 * this.chartSettings.height) / 12,
          },
        ],
        categoryBubblesMobile: {
          alternative: {
            x: (1.5 * this.chartSettings.width) / 6,
            y: (0.92 * this.chartSettings.height) / 6,
          },
          banking: {
            x: (4.4 * this.chartSettings.width) / 6,
            y: (0.87 * this.chartSettings.height) / 6,
          },
          blockchain: {
            x: (1.5 * this.chartSettings.width) / 6,
            y: (2.1 * this.chartSettings.height) / 6,
          },
          cryptocurrencies: {
            x: (4.4 * this.chartSettings.width) / 6,
            y: (2.1 * this.chartSettings.height) / 6,
          },
          finance: {
            x: (1.5 * this.chartSettings.width) / 6,
            y: (3.3 * this.chartSettings.height) / 6,
          },
          insur: {
            x: (4.3 * this.chartSettings.width) / 6,
            y: (3.3 * this.chartSettings.height) / 6,
          },
          investment: {
            x: (1.5 * this.chartSettings.width) / 6,
            y: (4.5 * this.chartSettings.height) / 6,
          },
          payments: {
            x: (4.2 * this.chartSettings.width) / 6,
            y: (4.65 * this.chartSettings.height) / 6,
          },
          reg: {
            x: (1.5 * this.chartSettings.width) / 6,
            y: (5.6 * this.chartSettings.height) / 6,
          },
        },
        categoryTextMobile: [
          {
            count: categoryValues.alternative.count,
            sum: categoryValues.alternative.sum,
            label: 'Alternative Lending',
            x: (1.5 * this.chartSettings.width) / 6,
            y: (0.12 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.banking.count,
            sum: categoryValues.banking.sum,
            label: 'Banking Tech',
            x: (4.5 * this.chartSettings.width) / 6,
            y: (0.12 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.blockchain.count,
            sum: categoryValues.blockchain.sum,
            label: 'Blockchain in Financial Services',
            x: (1.5 * this.chartSettings.width) / 6 + 5,
            y: (1.5 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.cryptocurrencies.count,
            sum: categoryValues.cryptocurrencies.sum,
            label: 'Cryptocurrencies',
            x: (4.5 * this.chartSettings.width) / 6,
            y: (1.5 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.finance.count,
            sum: categoryValues.finance.sum,
            label: 'Finance & Accounting Tech',
            x: (1.5 * this.chartSettings.width) / 6,
            y: (2.6 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.insur.count,
            sum: categoryValues.insur.sum,
            label: 'InsurTech',
            x: (4.5 * this.chartSettings.width) / 6,
            y: (2.6 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.investment.count,
            sum: categoryValues.investment.sum,
            label: 'Investment Tech',
            x: (1.5 * this.chartSettings.width) / 6,
            y: (3.85 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.payments.count,
            sum: categoryValues.payments.sum,
            label: 'Payments',
            x: (4.5 * this.chartSettings.width) / 6,
            y: (3.85 * this.chartSettings.height) / 6,
          },
          {
            count: categoryValues.reg.count,
            sum: categoryValues.reg.sum,
            label: 'RegTech',
            x: (1.5 * this.chartSettings.width) / 6,
            y: (5.3 * this.chartSettings.height) / 6,
          },
        ],
      };
    },
  },
  watch: {
    options: {
      handler(newVal) {
        // filter nodes according to new state
        if (newVal.selectedCountry === 'All') {
          this.currentNodes = this.allNodes.filter((d) => d.year === newVal.selectedYear);
        } else {
          this.currentNodes = this.allNodes.filter(
            (d) => d.country === newVal.selectedCountry && d.year === newVal.selectedYear
          );
        }

        // restart simulation
        this.simulation.stop();
        this.drawBase();
        d3.selectAll('.bubble-deals').remove();
        this.drawBubbleChart(this.currentNodes);
        this.filterTab();
      },
      deep: true,
    },
  },
  async mounted() {
    // Active buttons
    d3.select('#year-2020').classed('active', true);
    const csv = await d3.csv(`${process.env.BASE_URL}data/bubble-cluster.csv`);
    this.createNodes(csv);
    this.currentNodes = this.allNodes.filter((d) => d.year === '2020');
    this.drawBase();
    this.createSimulation();
    this.groupBubbles();
    this.drawBubbleChart(this.currentNodes);
  },
  methods: {
    drawBase() {
      d3.select('#chartSVG')
        .attr('width', this.chartSettings.width)
        .attr('height', this.chartSettings.height)
        .call(this.responsivefy);
    },
    filterTab() {
      if (this.options.selectedTab === 'All deals') {
        this.groupBubbles();
      } else if (this.options.selectedTab === 'Group by funding stage') {
        this.splitBubblesStage();
      } else if (this.options.selectedTab === 'Group by FinTech category') {
        this.splitBubblesCategory();
      }
    },
    createSimulation() {
      this.simulation = d3
        .forceSimulation()
        .velocityDecay(0.25)
        .force(
          'charge',
          d3.forceManyBody().strength((d) => -(d.radius ** 2.07) * this.forceStrength)
        )
        .force(
          'collision',
          d3.forceCollide().radius((d) => d.radius * 1.01)
        )
        .on('tick', this.ticked);

      this.simulation.stop();
    },
    createNodes(rawData) {
      const minAmount = d3.min(rawData, (d) => +d.amount);
      const maxAmount = d3.max(rawData, (d) => +d.amount);

      const radiusScale = d3.scalePow().exponent(this.radiusScale).range([5, 60]).domain([minAmount, maxAmount]);

      this.allNodes = rawData.map(function format(d) {
        return {
          radius: radiusScale(d.amount !== 'Undisclosed' ? +d.amount : 100000),
          value: d.amount,
          stage: kebabCase(d.stage).split('-')[0],
          round: d.round,
          country: d.country,
          company: d.company,
          date: d.date,
          founded_year: d.founded_year,
          year: d.year,
          category: kebabCase(d.category).split('-')[0],
          categoryFull: d.category,
          x: Math.random(),
          y: Math.random(),
        };
      });
    },
    drawBubbleChart(filteredNodes) {
      const bubbleColor = d3
        .scaleOrdinal()
        .domain(['Indonesia', 'Malaysia', 'Philippines', 'Singapore', 'Thailand', 'Vietnam'])
        .range(['#E25A5F', '#FDC4D0', '#A295E2', '#005EB8', '#F6C76A', '#F09252']);

      this.bubbles = d3.select('#chartSVG').selectAll('.bubble-deals').data(filteredNodes);

      const bubblesSVG = this.bubbles
        .enter()
        .append('circle')
        .classed('bubble-deals', true)
        .attr('r', 0)
        .attr('fill', (d) => (d.value !== 'Undisclosed' ? bubbleColor(d.country) : '#ffffff'))
        .attr('stroke', (d) => (d.value === 'Undisclosed' ? d3.rgb(bubbleColor(d.country)) : 'none'))
        .attr('stroke-width', 2);

      this.bubbles = this.bubbles.merge(bubblesSVG);

      this.bubbles
        .transition()
        .duration(800)
        .attr('r', (d) => d.radius);

      this.handleTooltip();

      this.simulation.nodes(filteredNodes);
    },
    handleTooltip() {
      this.bubbles
        .on('mouseenter.tooltip', function mouseenter(event, d) {
          d3.select(this).transition().duration(200).attr('stroke', '#201113').attr('stroke-width', '2');

          const selectedBubble = d3.select(this).node().getBoundingClientRect();

          const container = d3.select('#chartContainer').node().getBoundingClientRect();

          const tooltip = d3.select('.viz-tooltip').node().getBoundingClientRect();

          const posLeft =
            selectedBubble.left > document.body.clientWidth / 2
              ? selectedBubble.left - container.left - tooltip.width // right
              : selectedBubble.right - container.left; // left

          const posBottom =
            selectedBubble.top - container.top > container.height / 2
              ? selectedBubble.bottom - container.top - tooltip.height // bottom
              : selectedBubble.top - container.top; // top

          d3.select('.viz-tooltip').style('top', `${posBottom}px`).style('left', `${posLeft}px`);

          d3.select('.viz-tooltip').style('visibility', 'visible');

          d3.select('.viz-tooltip-text').html(`
            Company name: ${d.company}<br>
            Funding date: ${d.date}<br>
            Round name: ${d.round}<br>
            Funding amount: ${d.value !== 'Undisclosed' ? `US$${(+d.value).toLocaleString()}` : 'Undisclosed'}<br>
            Country: ${d.country}<br>
            Year founded: ${d.founded_year}<br>
            Category: ${d.categoryFull}
          `);
        })
        .on('mouseleave.design', function mouseleave() {
          const bubbleColor = d3
            .scaleOrdinal()
            .domain(['Indonesia', 'Malaysia', 'Philippines', 'Singapore', 'Thailand', 'Vietnam'])
            .range(['#E25A5F', '#FDC4D0', '#A295E2', '#005EB8', '#F6C76A', '#F09252']);

          d3.select('.viz-tooltip').style('visibility', 'hidden');
          d3.select(this)
            .transition()
            .duration(200)
            .attr('stroke', (d) => (d.value === 'Undisclosed' ? d3.rgb(bubbleColor(d.country)) : 'none'))
            .attr('stroke-width', 2);
        });
    },
    drawHeaderText(selectedData, groupLabel) {
      d3.selectAll('g.headerText').remove();
      const groupText = d3
        .select('#chartSVG')
        .append('g')
        .attr('class', 'headerText')
        .selectAll('g')
        .data(this.splitCenterCoors[selectedData])
        .enter()
        .append('g')
        .attr('class', `${groupLabel}`);

      if (groupLabel === 'All deals') {
        groupText
          .attr('transform', this.$breakpoint.mobile ? 'translate(-30, 0)' : 'translate(0, 0)');
        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', this.$breakpoint.mobile ? (d) => d.x - 100 : (d) => d.x - 60)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y + 44 : (d) => d.y + 20)
          .text('Deals')

        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', this.$breakpoint.mobile ? (d) => d.x + 100 : (d) => d.x + 40)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y + 44 : (d) => d.y + 20)
          .text('Total fundings*');
        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', this.$breakpoint.mobile ? (d) => d.x - 100 : (d) => d.x - 60)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y : (d) => d.y)
          .attr('font-weight', 'bold')
          .text((d) => d.count)
          .attr('class', 'value-text')

        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', this.$breakpoint.mobile ? (d) => d.x + 100 : (d) => d.x + 40)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y : (d) => d.y)
          .attr('font-weight', 'bold')
          .text((d) => (d.sum < 1000 ? `US$${d.sum} million` : `US$${(d.sum / 1000).toFixed(2)} billion`))
          .attr('class', 'value-text');

      } else {
        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('font-weight', 'bold')
          .attr('x', (d) => d.x)
          .attr('y', (d) => d.y)
          .text((d) => d.label);

        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', (d) => d.x)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y + 30 : (d) => d.y + 15)
          .text((d) => (d.count === 1 ? `${d.count} deal` : `${d.count} deals`));

        groupText
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('fill', 'black')
          .attr('x', (d) => d.x)
          .attr('y', this.$breakpoint.mobile ? (d) => d.y + 60 : (d) => d.y + 30)
          .text((d) => (d.count !== 0 ? `US$${d.sum} million` : ''))
          .attr('class', 'value-text');
      }
    },
    groupBubbles() {
      this.simulation
        .force(
          'x',
          d3
            .forceX()
            .strength(this.forceStrength)
            .x((d) => this.centerCoor[d.country].x)
        )
        .force(
          'y',
          d3
            .forceY()
            .strength(this.forceStrength)
            .y((d) => this.centerCoor[d.country].y)
        )
        .alpha(2)
        .restart();
      this.drawHeaderText('allText', 'All deals');
    },
    splitBubblesCategory() {
      if (this.$breakpoint.mobile) {
        this.simulation.force(
          'x',
          d3
            .forceX()
            .strength(0.1)
            .x((d) => this.splitCenterCoors.categoryBubblesMobile[d.category].x)
        );
        this.simulation.force(
          'y',
          d3
            .forceY()
            .strength(0.1)
            .y((d) => this.splitCenterCoors.categoryBubblesMobile[d.category].y)
        );
        this.simulation.alpha(1).restart();
        this.drawHeaderText('categoryTextMobile', 'Group by FinTech category');
      } else {
        this.simulation.force(
          'x',
          d3
            .forceX()
            .strength(0.1)
            .x((d) => this.splitCenterCoors.categoryBubbles[d.category].x)
        );
        this.simulation.force(
          'y',
          d3
            .forceY()
            .strength(0.1)
            .y((d) => this.splitCenterCoors.categoryBubbles[d.category].y)
        );
        this.simulation.alpha(1).restart();
        this.drawHeaderText('categoryText', 'Group by FinTech category');
        this.drawHeaderText('categoryText', 'Group by FinTech category');
        this.drawHeaderText('categoryText', 'Group by FinTech category');
      }
    },
    splitBubblesStage() {
      this.simulation.force(
        'x',
        d3
          .forceX()
          .strength(this.forceStrength)
          .x((d) => this.splitCenterCoors.stageBubbles[d.stage].x)
      );
      this.simulation.force(
        'y',
        d3
          .forceY()
          .strength(this.forceStrength)
          .y((d) => this.splitCenterCoors.stageBubbles[d.stage].y)
      );
      // @v4 We can reset the alpha value and restart the simulation
      this.simulation.alpha(1).restart();
      this.drawHeaderText('stageText', 'Group by funding stage');
    },
    ticked() {
      this.bubbles
        .attr('cx', function assignx(d) {
          return d.x;
        })
        .attr('cy', function assigny(d) {
          return d.y;
        });
    },
    responsivefy(svg) {
      const width = parseInt(svg.style('width'), 10);
      const height = parseInt(svg.style('height'), 10);
      const aspect = width / height;

      svg.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMinYMid').call(this.resize, aspect);

      d3.select(window).on('resize.test', () => {
        this.resize(svg, aspect);
      });
    },
    resize(svg, aspect) {
      const container = d3.select(svg.node().parentNode);
      const w = parseInt(container.style('width'), 10);

      svg.attr('width', w);
      svg.attr('height', Math.round(w / aspect));
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-funding__legend {
  @media #{map-get($media-breakpoints, 'mobile')} {
    ::v-deep .control-legend__item {
      &:not(.control-legend__item--reset) {
        flex-basis: 25% !important;
      }
    }
  }
}
.chart-funding__legend-deal {
  .legend-deal__icon {
    display: inline-block;
    width: 10px;
    height: 10px;
    border: 2px solid #005eb8;
    border-radius: 99px;
    margin-right: 5px;
  }
}

#chartContainer {
  margin-top: 60px;
  position: relative;
}

.viz-tooltip {
  position: absolute;
  pointer-events: none;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 10px;
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  visibility: hidden;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  
  @media #{map-get($media-breakpoints, 'mobile')} {
    width: 180px;
    display: block;
  }

  &-text {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 135%;
    color: #373839;
    flex: none;
    order: 0;
    align-self: center;
    flex-grow: 0;
  }
}
</style>

<style lang="scss">
.chart-funding {
  text {
    font-family: 'Noto Sans Regular', sans-serif;
  }

  .headerText text {
    font-size: 12.5px;
    font-family: 'Noto Sans Bold', sans-serif;

    @media #{map-get($media-breakpoints, 'mobile')} {
      font-size: 28.5px;
    }
  }

  .headerText text.value-text {
    font-family: 'Noto Sans Regular', sans-serif;
  }
  
  .viz-tooltip-text {
    font-size: 13px;
    line-height: 18px;
    font-family: 'Noto Sans Regular', sans-serif;
  }
}
</style>
